import {
  HHButton,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import Autocomplete from '@mui/material/Autocomplete';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  ClientType,
  FeeScheduleEntityType,
  PartnershipType,
  PaymentSystemType,
  useGetPaymentTypesQuery,
} from '../../types';
import { feeScheduleFilterBarSchema } from './fee-schedule-filter-bar-validation-schema';

export interface FeeScheduleFilterParams {
  entityIds: number[];
  paymentTypes: string[];
  paymentSystems: string[];
  status: string;
  priceMin: string;
  priceMax: string;
}

export const defaultFeeScheduleFilters: FeeScheduleFilterParams = {
  entityIds: [],
  paymentTypes: [],
  paymentSystems: [],
  status: 'all',
  priceMin: '',
  priceMax: '',
};

interface FilterFormValues {
  entities: { id: number; name: string }[];
  paymentTypes: string[];
  paymentSystems: string[];
  status: string;
  priceMin: string;
  priceMax: string;
}

const STATUS_OPTIONS = ['all', 'enabled', 'disabled'] as const;

const PAYMENT_SYSTEM_OPTIONS = Object.values(PaymentSystemType);

const ENTITY_AUTOCOMPLETE_SX = {
  flex: '3 1 200px',
  minWidth: 160,
} as const;

const PAYMENT_SYSTEM_AUTOCOMPLETE_SX = {
  flex: '1.5 1 160px',
  minWidth: 140,
  maxWidth: 240,
} as const;

interface FeeScheduleFilterBarProps {
  filterParams: FeeScheduleFilterParams;
  setFilterParams: Dispatch<SetStateAction<FeeScheduleFilterParams>>;
  entityType: FeeScheduleEntityType;
  clients: ClientType[];
  clientsLoading: boolean;
  partnerships: PartnershipType[];
  partnershipsLoading: boolean;
  onFiltersApplied: () => void;
}

export const FeeScheduleFilterBar = ({
  filterParams,
  setFilterParams,
  entityType,
  clients,
  clientsLoading,
  partnerships,
  partnershipsLoading,
  onFiltersApplied,
}: FeeScheduleFilterBarProps): JSX.Element => {
  const entityOptions: { id: number; name: string }[] =
    entityType === FeeScheduleEntityType.Client
      ? clients.map(c => ({ id: c.id, name: c.name }))
      : partnerships.map(p => ({ id: p.id, name: p.name }));

  const { data: paymentTypesData, loading: paymentTypesLoading } =
    useGetPaymentTypesQuery();

  const paymentTypeOptions = useMemo(
    () =>
      (paymentTypesData?.getPaymentTypes ?? [])
        .map(pt => pt.code)
        .sort((a, b) => a.localeCompare(b)),
    [paymentTypesData],
  );

  const { handleSubmit, control, formState, reset, trigger } =
    useForm<FilterFormValues>({
      mode: 'onChange',
      defaultValues: {
        entities: [],
        paymentTypes: [],
        paymentSystems: [],
        status: 'all',
        priceMin: '',
        priceMax: '',
      },
      resolver: yupResolver(feeScheduleFilterBarSchema),
    });

  const { isDirty, isValid, errors } = formState;

  useEffect(() => {
    const options =
      entityType === FeeScheduleEntityType.Client
        ? clients.map(c => ({ id: c.id, name: c.name }))
        : partnerships.map(p => ({ id: p.id, name: p.name }));

    reset({
      entities: options.filter(e => filterParams.entityIds.includes(e.id)),
      paymentTypes: filterParams.paymentTypes,
      paymentSystems: filterParams.paymentSystems,
      status: filterParams.status,
      priceMin: filterParams.priceMin,
      priceMax: filterParams.priceMax,
    });
  }, [filterParams, entityType, clients, partnerships, reset]);

  const onApplyClick = (): void => {
    handleSubmit(data => {
      const formattedData: FeeScheduleFilterParams = {
        entityIds: data.entities.map(e => e.id),
        paymentTypes: data.paymentTypes,
        paymentSystems: data.paymentSystems,
        status: data.status,
        priceMin: data.priceMin,
        priceMax: data.priceMax,
      };

      setFilterParams(formattedData);
      onFiltersApplied();
      reset(data);
    })();
  };

  const handleReset = (): void => {
    reset({
      entities: [],
      paymentTypes: [],
      paymentSystems: [],
      status: 'all',
      priceMin: '',
      priceMax: '',
    });
    setFilterParams(defaultFeeScheduleFilters);
    onFiltersApplied();
  };

  const isFiltersDefault =
    filterParams.entityIds.length === 0 &&
    filterParams.paymentTypes.length === 0 &&
    filterParams.paymentSystems.length === 0 &&
    filterParams.status === 'all' &&
    filterParams.priceMin === '' &&
    filterParams.priceMax === '';

  return (
    <HHStack
      direction="row"
      alignItems="flex-end"
      flexWrap="wrap"
      sx={{ gap: 2, flex: 1, minWidth: 0, paddingRight: 6 }}
    >
      <Controller
        control={control}
        name="entities"
        render={({ field: { onChange, value } }): JSX.Element => (
          <Autocomplete
            multiple
            id="entity-filter"
            data-testid="entity-filter"
            value={value}
            options={entityOptions}
            size="small"
            sx={ENTITY_AUTOCOMPLETE_SX}
            isOptionEqualToValue={(option, val): boolean =>
              option.id === val.id
            }
            limitTags={1}
            loading={
              entityType === FeeScheduleEntityType.Client
                ? clientsLoading
                : partnershipsLoading
            }
            onChange={(_, newValue): void => onChange(newValue)}
            getOptionLabel={(option): string => option.name}
            renderInput={(params): JSX.Element => (
              <HHTextField
                {...params}
                hhVariant="variant-bypass"
                variant="standard"
                label={
                  entityType === FeeScheduleEntityType.Client
                    ? 'Client'
                    : 'Partnership'
                }
              />
            )}
          />
        )}
      />

      <Controller
        control={control}
        name="paymentTypes"
        render={({ field: { onChange, value } }): JSX.Element => (
          <Autocomplete
            multiple
            id="payment-type-filter"
            data-testid="payment-type-filter"
            value={value}
            options={paymentTypeOptions}
            size="small"
            sx={{ flex: '1.5 1 160px', minWidth: 140, maxWidth: 240 }}
            limitTags={1}
            loading={paymentTypesLoading}
            onChange={(_, newValue): void => onChange(newValue)}
            getOptionLabel={(option): string => option}
            renderInput={(params): JSX.Element => (
              <HHTextField
                {...params}
                hhVariant="variant-bypass"
                variant="standard"
                label="Payment Type"
              />
            )}
          />
        )}
      />

      <Controller
        control={control}
        name="paymentSystems"
        render={({ field: { onChange, value } }): JSX.Element => (
          <Autocomplete
            multiple
            id="payment-system-filter"
            data-testid="payment-system-filter"
            value={value}
            options={PAYMENT_SYSTEM_OPTIONS}
            size="small"
            sx={PAYMENT_SYSTEM_AUTOCOMPLETE_SX}
            limitTags={1}
            onChange={(_, newValue): void => onChange(newValue)}
            getOptionLabel={(option): string => option}
            renderInput={(params): JSX.Element => (
              <HHTextField
                {...params}
                hhVariant="variant-bypass"
                variant="standard"
                label="Payment System"
              />
            )}
          />
        )}
      />

      <Controller
        control={control}
        name="status"
        render={({ field: { onChange, value } }): JSX.Element => (
          <Autocomplete
            id="status-filter"
            data-testid="status-filter"
            value={value}
            options={[...STATUS_OPTIONS]}
            size="small"
            sx={{ flex: '0 0 auto', minWidth: 110, maxWidth: 140 }}
            disableClearable
            onChange={(_, newValue): void => onChange(newValue)}
            getOptionLabel={(option): string =>
              option.charAt(0).toUpperCase() + option.slice(1)
            }
            renderInput={(params): JSX.Element => (
              <HHTextField
                {...params}
                hhVariant="variant-bypass"
                variant="standard"
                label="Status"
              />
            )}
          />
        )}
      />

      <HHStack direction="row" spacing={1} alignItems="flex-end">
        <Controller
          control={control}
          name="priceMin"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="standard"
              type="number"
              label="Price Min"
              value={value}
              onChange={(e): void => {
                onChange(e.target.value);
                trigger(['priceMin', 'priceMax']);
              }}
              error={!!errors.priceMin}
              helperText={errors.priceMin?.message ?? ''}
              sx={{ flex: '0 0 auto', minWidth: 80, maxWidth: 110 }}
              size="small"
              inputProps={{ 'aria-label': 'Price Min' }}
            />
          )}
        />
        <Controller
          control={control}
          name="priceMax"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="standard"
              type="number"
              label="Price Max"
              value={value}
              onChange={(e): void => {
                onChange(e.target.value);
                trigger(['priceMin', 'priceMax']);
              }}
              error={!!errors.priceMax}
              helperText={errors.priceMax?.message ?? ''}
              sx={{ flex: '0 0 auto', minWidth: 80, maxWidth: 110 }}
              size="small"
              inputProps={{ 'aria-label': 'Price Max' }}
            />
          )}
        />
      </HHStack>

      <HHStack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ marginLeft: 'auto', flexShrink: 0 }}
      >
        <HHButton
          size="medium"
          hhVariant="contained"
          onClick={onApplyClick}
          disabled={!isDirty || !isValid}
        >
          Apply
        </HHButton>
        <HHButton
          size="medium"
          hhVariant="outlined"
          onClick={handleReset}
          disabled={isFiltersDefault && !isDirty}
        >
          Reset
        </HHButton>
      </HHStack>
    </HHStack>
  );
};
